import {
  CountAssetsRequest,
  CountAssetsResponse
} from '@dtx-company/connect-codegen/src/gen/asset/v1/asset_pb'
import {
  CountFoldersRequestSchema,
  CountFoldersResponse,
  ListFoldersRequest
} from '@dtx-company/connect-codegen/src/gen/container/v1/container_pb'
import { create } from '@bufbuild/protobuf'
import { logger } from '@dtx-company/logger'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import assetsClient from '@dtx-company/inter-app/src/connect-client/assetsClient'
import containerClient from '@dtx-company/inter-app/src/connect-client/containerClient'
import useSWR, { KeyedMutator } from 'swr'

/*
Fetches total asset and folder counts for query (regardless of number of paginated results loaded)
*/

export const useTotalAssetCounts = (
  args?: CountAssetsRequest & {
    skip?: boolean
  }
): {
  totalAssetCount: number
  mutateAssetCount?: KeyedMutator<CountAssetsResponse | undefined>
} => {
  const { token } = useAuthState()
  const { data, error, mutate } = useSWR(
    () =>
      token && args && !args.skip && Boolean((args.id?.value ?? '').length)
        ? [`asset_total_count`, args]
        : undefined,
    async ([_key, args]) => {
      const resp = await assetsClient.countAssets(args, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (resp) {
        return resp
      }
    },
    {
      onError(error) {
        logger.logError(error, { tag: 'unified_asset_management', technicalArea: 'general' })
      },
      onErrorRetry: error => {
        // Never retry on [unauthenticated] HTTP 401, retry is disabled in useInfiniteFetchAssets
        if (error.message === '[unauthenticated] HTTP 401') return
      }
    }
  )

  if (error) {
    return { mutateAssetCount: undefined, totalAssetCount: 0 }
  }
  return { totalAssetCount: data?.numAssets ?? 0, mutateAssetCount: mutate }
}

export const useTotalFolderCounts = (
  args?: ListFoldersRequest & {
    skip?: boolean
  }
): {
  totalFolderCount: number
  mutateFolderCount?: KeyedMutator<CountFoldersResponse | undefined>
} => {
  const { token } = useAuthState()
  const { data, error, mutate } = useSWR(
    () => (token && args && !args.skip ? [`folder_total_count`, args] : undefined),
    async ([_key, args]) => {
      const countRequest = create(CountFoldersRequestSchema, {
        id: args.id,
        filter: args.filter
      })
      const resp = await containerClient.countFolders(countRequest, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (resp) {
        return resp
      }
    }
  )

  if (error) {
    logger.logError(error, { tag: 'unified_asset_management', technicalArea: 'general' })
    return { mutateFolderCount: undefined, totalFolderCount: 0 }
  }

  return { totalFolderCount: data?.numFolders ?? 0, mutateFolderCount: mutate }
}
